import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, FolderKanban, Calendar, IndianRupee, CheckSquare } from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR, formatDate } from '@/utils/formatters';

interface ProjectDetail {
  id: string;
  name: string;
  description?: string;
  status: string;
  progress?: number;
  startDate?: string;
  deadline?: string;
  budget?: number;
  createdAt: string;
  updatedAt: string;
  account?: { id: string; name: string } | null;
  tasks?: { id: string; title: string; status: string; priority: string; dueDate?: string }[];
}

const STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  not_started: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Not Started' },
  in_progress: { bg: 'bg-blue-50', text: 'text-blue-700', label: 'In Progress' },
  on_hold: { bg: 'bg-yellow-50', text: 'text-yellow-700', label: 'On Hold' },
  completed: { bg: 'bg-green-50', text: 'text-green-700', label: 'Completed' },
};

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProject = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/projects/${id}`);
      setProject(res.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchProject(); }, [fetchProject]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
        <span className="ml-2 text-sm text-gray-500">Loading project...</span>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-red-500">
        <p className="text-sm">{error || 'Project not found'}</p>
        <button onClick={() => navigate('/projects')} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Back to Projects</button>
      </div>
    );
  }

  const st = STATUS_COLORS[project.status] || STATUS_COLORS.not_started;
  const progress = project.progress ?? 0;

  return (
    <div>
      <div className="mb-6 flex items-center gap-4">
        <button onClick={() => navigate('/projects')} className="rounded-lg border border-gray-300 p-2 text-gray-500 hover:bg-gray-50">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
          <p className="text-sm text-gray-500">Created {formatDate(project.createdAt)}</p>
        </div>
        <span className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${st.bg} ${st.text}`}>{st.label}</span>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            {project.description && <p className="mb-4 text-sm text-gray-600">{project.description}</p>}
            <div className="mb-4">
              <div className="mb-1 flex items-center justify-between text-sm">
                <span className="text-gray-500">Progress</span>
                <span className="font-medium text-gray-700">{progress}%</span>
              </div>
              <div className="h-2 w-full rounded-full bg-gray-100">
                <div className="h-2 rounded-full bg-[var(--ink-0)] transition-all" style={{ width: `${Math.min(progress, 100)}%` }} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Calendar size={16} className="text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Start Date</p>
                  <p className="text-sm text-gray-900">{project.startDate ? formatDate(project.startDate) : '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar size={16} className="text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Deadline</p>
                  <p className="text-sm text-gray-900">{project.deadline ? formatDate(project.deadline) : '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <IndianRupee size={16} className="text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Budget</p>
                  <p className="text-sm text-gray-900">{project.budget != null ? formatINR(project.budget) : '-'}</p>
                </div>
              </div>
              {project.account && (
                <div className="flex items-center gap-3">
                  <FolderKanban size={16} className="text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Account</p>
                    <p className="text-sm text-gray-900">{project.account.name}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Tasks */}
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Tasks</h2>
            {project.tasks && project.tasks.length > 0 ? (
              <div className="space-y-2">
                {project.tasks.map(task => (
                  <div key={task.id} className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 p-3">
                    <div className="flex items-center gap-3">
                      <CheckSquare size={16} className={task.status === 'done' ? 'text-green-500' : 'text-gray-400'} />
                      <span className="text-sm text-gray-900">{task.title}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                        task.priority === 'critical' ? 'bg-red-50 text-red-700' :
                        task.priority === 'high' ? 'bg-orange-50 text-orange-700' :
                        task.priority === 'medium' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'
                      }`}>{task.priority}</span>
                      {task.dueDate && <span className="text-xs text-gray-500">{formatDate(task.dueDate)}</span>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No tasks for this project yet.</p>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h3 className="mb-4 text-sm font-semibold text-gray-900">Details</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Created</span><span className="text-gray-900">{formatDate(project.createdAt)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Updated</span><span className="text-gray-900">{formatDate(project.updatedAt)}</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
